import React, { useContext, useEffect, useMemo, useState } from "react";
import "./styles/SingleReleaseTextsModal.scss";

import ReleaseTextIcon from "../../../../components/VectorIcons/ReleaseTextIcon";
import SimpleScriptBox from "../../SimpleScriptBox/SimpleScriptBox";
import SimpleButton from "../../../../components/SimpleButton/SimpleButton";
import Switch from "../../../../components/Switch/Switch";
import Textarea from "../../../../components/Textarea/Textarea";
import AppContext from "../../../../context/appContext";
import { Box, Tooltip } from "@mui/material";
import { sendEvent } from "../../../../services/GoogleTagManager/methods";
import { releaseAttachments } from "../../../../services/api/attachments/attachments.api";
import CheckBox from "../../../../components/CheckBox/CheckBox";
import {
  formatDate,
  getSingleReleaseToastMessage,
  toastError,
  toastSuccess,
  PERMISSION_DENIED_MESSAGE,
} from "../../../../utils";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";

const RELEASE_DESTINATIONS = [
  {
    key: "channel",
    label: "Canal",
    releaseType: "Channel",
  },
  {
    key: "production",
    label: "Produção",
    releaseType: "Production",
  },
  {
    key: "cultureHub",
    label: "HUB Cultura",
    releaseType: "CultureHub",
  },
];

const createDestinationState = (destinations, getValue = () => false) =>
  destinations.reduce(
    (state, destination) => ({
      ...state,
      [destination.key]: getValue(destination),
    }),
    {}
  );

const normalizeAttachment = (attachment) => ({
  ...attachment,
  releaseManagement: {
    ...attachment.releaseManagement,
    ...createDestinationState(RELEASE_DESTINATIONS, ({ key }) =>
      Boolean(attachment.releaseManagement?.[key])
    ),
  },
});

const ReleaseTableHeader = ({
  title,
  selectedDestinations,
  onToggleAll,
  visibleDestinations,
}) => (
  <div
    className="releaseTableHeader"
    style={{
      "--release-destination-count": visibleDestinations.length,
    }}
  >
    <strong>{title}</strong>

    {visibleDestinations.map(({ key, label }) => (
      <div className="releaseHeaderOption" key={key}>
        <div className="releaseHeaderCheckbox">
          <CheckBox
            isActive={selectedDestinations[key]}
            value={key}
            event={() => onToggleAll(key)}
            noMargin
          />
        </div>

        <span className="releaseHeaderLabel">{label}</span>
      </div>
    ))}
  </div>
);

const ReleaseDestinationSection = ({
  title,
  attachment,
  onChange,
  showHeader,
  selectedDestinations,
  onToggleAll,
  visibleDestinations,
  isAddendum,
  association,
  associationsLabel,
  isLoadingAssociations,
}) => (
  <section className="releaseDestinationSection">
    {showHeader && (
      <ReleaseTableHeader
        title={title}
        selectedDestinations={selectedDestinations}
        onToggleAll={onToggleAll}
        visibleDestinations={visibleDestinations}
      />
    )}

    <ReleaseDestinationRow
      attachment={attachment}
      onChange={onChange}
      visibleDestinations={visibleDestinations}
      isAddendum={isAddendum}
      associationsLabel={associationsLabel}
      isLoadingAssociations={isLoadingAssociations}
    />
  </section>
);

const ReleaseDestinationRow = ({
  attachment,
  onChange,
  visibleDestinations,
  isAddendum,
  associationsLabel,
}) => {
  const {
    id,
    fileName,
    blockName,
    blockNumber,
    version,
    createdAt,
    releaseManagement,
  } = attachment;

  return (
    <div
      className="releaseDestinationRow"
      style={{
        "--release-destination-count": visibleDestinations.length,
      }}
    >
      <div className="attachmentInformation">
        <span style={{ marginLeft: 8 }}>{fileName}</span>

        <span>
          {!isAddendum && blockNumber != null && <>Bloco {blockNumber}</>}
        </span>

        {isAddendum ? (
          associationsLabel && <span>{associationsLabel}</span>
        ) : (
          <>
            {blockName && <span>{blockName}</span>}

            {version != null && <span>{version}ª Versão</span>}
          </>
        )}

        <span>{formatDate(createdAt)}</span>
      </div>

      {visibleDestinations.map(({ key }) => (
        <ReleaseDestinationCheckBox
          key={key}
          attachmentId={id}
          destination={key}
          checked={Boolean(releaseManagement?.[key])}
          onChange={onChange}
        />
      ))}
    </div>
  );
};

const ReleaseDestinationCheckBox = ({
  attachmentId,
  destination,
  checked,
  onChange,
}) => (
  <div className="releaseOption">
    <div className="releaseHeaderCheckbox">
      <CheckBox
        isActive={checked}
        value={destination}
        event={() =>
          onChange({
            attachmentId,
            destination,
            checked: !checked,
          })
        }
        noMargin
      />

      <span className="releaseCheckboxPlaceholder">{""}</span>
    </div>
  </div>
);

const SingleReleaseTextsModal = ({
  releaseTexts,
  setStateOpen,
  reload,
  projectType,
  submitCallback,
  releaseContext,
  contentId,
}) => {
  const appContext = useContext(AppContext);

  const { enabled: TEXT_RELEASE_V2_ENABLED, loading: TEXT_RELEASE_V2_LOADING } =
    useFeatureFlag("TEXT_RELEASE_V2");

  const {
    enabled: CULTURE_HUB_RELEASE_ENABLED,
    loading: CULTURE_HUB_RELEASE_LOADING,
  } = useFeatureFlag("CULTURE_HUB_RELEASE");

  const visibleDestinations = RELEASE_DESTINATIONS.filter(
    ({ key }) =>
      (key !== "production" || TEXT_RELEASE_V2_ENABLED) &&
      (key !== "cultureHub" || CULTURE_HUB_RELEASE_ENABLED)
  );

  const [attachments, setAttachments] = useState(() => {
    const current = normalizeAttachment(releaseTexts.current);

    const versions = (releaseTexts.versions ?? [])
      .filter((version) => version.id !== releaseTexts.current.id)
      .filter((version) =>
        visibleDestinations.some(({ key }) =>
          Boolean(version.releaseManagement?.[key])
        )
      )
      .map(normalizeAttachment);

    return {
      current,
      versions,
    };
  });

  const [observationsEnabled, setObservationsEnabled] = useState(() =>
    createDestinationState(RELEASE_DESTINATIONS)
  );

  const [observations, setObservations] = useState(() =>
    createDestinationState(
      RELEASE_DESTINATIONS,
      ({ key }) =>
        releaseTexts.current?.releaseManagement?.[`${key}Observation`] ?? ""
    )
  );

  const isValid = () =>
    getAllAttachments().some((attachment) => {
      const original =
        originalStates[attachment.id] ??
        createDestinationState(RELEASE_DESTINATIONS);

      return visibleDestinations.some(
        ({ key }) =>
          Boolean(attachment.releaseManagement?.[key]) !==
          Boolean(original[key])
      );
    });

  const [sentAnalytics, setSentAnalytics] = useState(false);

  const getOriginalReleaseState = (attachment) =>
    createDestinationState(RELEASE_DESTINATIONS, ({ key }) =>
      Boolean(attachment.releaseManagement?.[key])
    );

  const [originalStates] = useState(() => {
    const map = {};

    if (releaseTexts.current) {
      map[releaseTexts.current.id] = getOriginalReleaseState(
        releaseTexts.current
      );
    }

    if (releaseTexts.versions) {
      releaseTexts.versions.forEach((version) => {
        map[version.id] = getOriginalReleaseState(version);
      });
    }

    return map;
  });

  const hasReleaseByDestination = useMemo(
    () => ({
      channel:
        Boolean(attachments.current.releaseManagement?.channel) &&
          !Boolean(originalStates[attachments.current.id]?.channel) ||
        attachments.versions.some(
          (version) =>
            Boolean(version.releaseManagement?.channel) &&
            !Boolean(originalStates[version.id]?.channel)
        ),
      production:
        Boolean(attachments.current.releaseManagement?.production) &&
          !Boolean(originalStates[attachments.current.id]?.production) ||
        attachments.versions.some(
          (version) =>
            Boolean(version.releaseManagement?.production) &&
            !Boolean(originalStates[version.id]?.production)
        ),
      cultureHub:
        Boolean(attachments.current.releaseManagement?.cultureHub) &&
          !Boolean(originalStates[attachments.current.id]?.cultureHub) ||
        attachments.versions.some(
          (version) =>
            Boolean(version.releaseManagement?.cultureHub) &&
            !Boolean(originalStates[version.id]?.cultureHub)
        ),
    }),
    [attachments, originalStates]
  );

  useEffect(() => {
    setObservationsEnabled((previousState) => {
      const nextState = { ...previousState };
      let changed = false;

      visibleDestinations.forEach(({ key }) => {
        if (!hasReleaseByDestination[key] && nextState[key]) {
          nextState[key] = false;
          changed = true;
        }
      });

      return changed ? nextState : previousState;
    });
  }, [hasReleaseByDestination, visibleDestinations]);

  const isAddendum = releaseContext?.type === "planningAddendum";

  const associations = releaseContext?.associations ?? [];

  const associationsLabel = useMemo(() => {
    if (!associations.length) {
      return "";
    }

    const numbers = associations
      .map((association) => association.number)
      .filter((number) => number != null)
      .sort((a, b) => a - b);

    const scope = associations[0]?.scope;

    const label =
      scope === "chapter"
        ? numbers.length > 1
          ? "Capítulos"
          : "Capítulo"
        : numbers.length > 1
          ? "Episódios"
          : "Episódio";

    return `${label} ${numbers.join(", ")}`;
  }, [associations]);

  const updateAttachmentDestination = ({
    attachmentId,
    destination,
    checked,
  }) => {
    setAttachments((previousState) => {
      if (previousState.current.id === attachmentId) {
        return {
          ...previousState,
          current: {
            ...previousState.current,
            releaseManagement: {
              ...previousState.current.releaseManagement,
              [destination]: checked,
            },
          },
        };
      }

      return {
        ...previousState,
        versions: previousState.versions.map((version) =>
          version.id === attachmentId
            ? {
                ...version,
                releaseManagement: {
                  ...version.releaseManagement,
                  [destination]: checked,
                },
              }
            : version
        ),
      };
    });
  };

  const getAllAttachments = () => [
    attachments.current,
    ...attachments.versions,
  ];

  const buildReleasePayload = () => {
    const payload = {
      contentId,
      releases: [],
      unreleases: [],
    };

    getAllAttachments().forEach((attachment) => {
      const original =
        originalStates[attachment.id] ??
        createDestinationState(RELEASE_DESTINATIONS);

      const releaseTypes = {
        release: [],
        unrelease: [],
      };

      visibleDestinations.forEach(({ key, releaseType }) => {
        const previous = Boolean(original[key]);
        const current = Boolean(attachment.releaseManagement?.[key]);

        if (previous === current) {
          return;
        }

        const item = {
          releaseType,
          observation: observationsEnabled[key] ? observations[key] : "",
        };

        if (current) {
          releaseTypes.release.push(item);
        } else {
          releaseTypes.unrelease.push(item);
        }
      });

      const addItems = (target, types) => {
        const groupedByObservation = types.reduce(
          (groups, { releaseType, observation }) => {
            const groupKey = observation ?? "";

            return {
              ...groups,
              [groupKey]: [...(groups[groupKey] ?? []), releaseType],
            };
          },
          {}
        );

        Object.entries(groupedByObservation).forEach(
          ([observation, groupedReleaseTypes]) => {
            target.push({
              attachmentId: attachment.id,
              releaseType: groupedReleaseTypes,
              observation,
            });
          }
        );
      };

      addItems(payload.releases, releaseTypes.release);
      addItems(payload.unreleases, releaseTypes.unrelease);
    });

    return payload;
  };

  const handleAnalytics = () => {
    if (sentAnalytics) {
      return;
    }

    sendEvent(`Projeto de ${projectType}`, "Liberação de anexo");
    setSentAnalytics(true);
  };

  const sendRelease = async () => {
    const payload = buildReleasePayload();

    if (payload.releases.length === 0 && payload.unreleases.length === 0) {
      return;
    }

    try {
      appContext.setShowGlassLoading(true);

      await releaseAttachments(payload);

      handleAnalytics();

      toastSuccess(
        getSingleReleaseToastMessage({
          releases: payload.releases,
          unreleases: payload.unreleases,
          subject: isAddendum ? "Adendo" : "Anexo",
        })
      );

      appContext.setCurrentStepBox("closed");

      reload();

      if (submitCallback) {
        submitCallback();
      }
    } catch (error) {
      toastError(
        error?.response?.status === 403
          ? PERMISSION_DENIED_MESSAGE
          : isAddendum
            ? "Erro ao atualizar a liberação do adendo!"
            : "Erro ao atualizar a liberação dos anexos!"
      );

      if (submitCallback) {
        submitCallback();
      }
    } finally {
      appContext.setShowGlassLoading(false);
    }
  };

  const areAllVersionsSelected = (destination) =>
    attachments.versions.length > 0 &&
    attachments.versions.every((version) =>
      Boolean(version.releaseManagement?.[destination])
    );

  const getVersionsSelectedDestinations = () =>
    createDestinationState(visibleDestinations, ({ key }) =>
      areAllVersionsSelected(key)
    );

  const toggleAllVersionsByDestination = (destination) => {
    const shouldSelectAll = !areAllVersionsSelected(destination);

    setAttachments((previousState) => ({
      ...previousState,
      versions: previousState.versions.map((version) => ({
        ...version,
        releaseManagement: {
          ...version.releaseManagement,
          [destination]: shouldSelectAll,
        },
      })),
    }));
  };

  const toggleCurrentByDestination = (destination) => {
    setAttachments((previousState) => ({
      ...previousState,
      current: {
        ...previousState.current,
        releaseManagement: {
          ...previousState.current.releaseManagement,
          [destination]:
            !previousState.current.releaseManagement?.[destination],
        },
      },
    }));
  };

  if (TEXT_RELEASE_V2_LOADING || CULTURE_HUB_RELEASE_LOADING) {
    return null;
  }

  return (
    <SimpleScriptBox
      open={releaseTexts.isOpen}
      toggleScriptBox={() => setStateOpen(false)}
      title={isAddendum ? "Liberação de adendo" : "Liberação de texto"}
      size="large"
      icon={<ReleaseTextIcon color="#fff" />}
      removePadding
    >
      <div id="ReleaseTextsModal">
        <div className="releaseTextsModalContent">
          <ReleaseDestinationSection
            title="Marque o local para onde deseja liberar o texto"
            attachment={attachments.current}
            onChange={updateAttachmentDestination}
            selectedDestinations={attachments.current.releaseManagement}
            onToggleAll={toggleCurrentByDestination}
            visibleDestinations={visibleDestinations}
            showHeader
            isAddendum={isAddendum}
            associationsLabel={associationsLabel}
          />

          {!isAddendum && attachments.versions.length > 0 && (
            <div className="previousVersionsSection">
              <ReleaseTableHeader
                title="Manter demais versões liberadas?"
                selectedDestinations={getVersionsSelectedDestinations()}
                onToggleAll={toggleAllVersionsByDestination}
                visibleDestinations={visibleDestinations}
              />

              {attachments.versions.map((version) => (
                <ReleaseDestinationRow
                  key={version.id}
                  attachment={version}
                  onChange={updateAttachmentDestination}
                  visibleDestinations={visibleDestinations}
                />
              ))}
            </div>
          )}

          <div className="releaseObservations">
            <h4>Adicionar observação (opcional)</h4>

            <div className="observationSwitches">
              {visibleDestinations.map(({ key, label }) => {
                const disabled = !hasReleaseByDestination[key];

                const switchEl = (
                  <Switch
                    key={key}
                    blockLabel={label}
                    event={() =>
                      setObservationsEnabled((previousState) => ({
                        ...previousState,
                        [key]: !previousState[key],
                      }))
                    }
                    switchOn={observationsEnabled[key]}
                    disabled={disabled}
                  />
                );

                if (!disabled) {
                  return switchEl;
                }

                return (
                  <Tooltip
                    key={key}
                    title="Habilite a liberação deste destino para adicionar observações"
                    arrow
                  >
                    <Box>{switchEl}</Box>
                  </Tooltip>
                );
              })}
            </div>

            {visibleDestinations.map(({ key, label }) => {
              if (!observationsEnabled[key]) {
                return null;
              }

              return (
                <Textarea
                  key={key}
                  maxRows={12}
                  label={`Observação para ${label}`}
                  placeholder={`Escreva aqui a observação para ${label}`}
                  value={observations[key]}
                  handleChange={(event) => {
                    const { value } = event.target;

                    setObservations((previousState) => ({
                      ...previousState,
                      [key]: value,
                    }));
                  }}
                />
              );
            })}
          </div>
        </div>

        <div className="releaseTextsFooter">
          <SimpleButton
            text="Voltar"
            classButton="buttonCancel medium"
            clickEvent={() => setStateOpen(false)}
          />

          <SimpleButton
            text="Salvar"
            classButton="buttonGreen medium"
            toValidate
            isValidForm={isValid()}
            clickEvent={sendRelease}
          />
        </div>
      </div>
    </SimpleScriptBox>
  );
};

export default SingleReleaseTextsModal;
